import React, { useState, useEffect } from 'react';
import { useControleDemanda } from '../context/ControleDemandaProvider';
import supabase from '../../../config/supabaseClient';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, AreaChart, Area
} from 'recharts';

import MetricasTempoReal from './MetricasTempoReal';
import AlertasDashboard from './AlertasDashboard';

export default function DashboardEstatisticas() {
  const { loading } = useControleDemanda();
  
  const [estatisticas, setEstatisticas] = useState({
    resumoGeral: {},
    demandasPorStatus: [],
    demandasPorSetor: [],
    evolucaoMensal: [],
    topBairros: [],
    topCategorias: [],
    mapaCalor: [],
    mapaPontos: [],
    tempoMedioResolucao: 0
  });
  
  const [loadingStats, setLoadingStats] = useState(true);

  useEffect(() => {
    carregarEstatisticas();
  }, []);

  const carregarEstatisticas = async () => {
    setLoadingStats(true);
    try {
      const [
        resumoGeral,
        demandasPorStatus,
        demandasPorSetor,
        evolucaoMensal,
        topBairros,
        topCategorias,
        mapaCalor,
        mapaPontos,
        tempoMedio
      ] = await Promise.all([
        carregarResumoGeral(),
        carregarDemandasPorStatus(),
        carregarDemandasPorSetor(),
        carregarEvolucaoMensal(),
        carregarTopBairros(),
        carregarTopCategorias(),
        carregarMapaCalor(),
        carregarMapaPontos(),
        carregarTempoMedioResolucao()
      ]);

      setEstatisticas({
        resumoGeral,
        demandasPorStatus,
        demandasPorSetor,
        evolucaoMensal,
        topBairros,
        topCategorias,
        mapaCalor,
        mapaPontos,
        tempoMedioResolucao: tempoMedio
      });
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const carregarResumoGeral = async () => {
    const { data: demandas } = await supabase.from('view_demandas_detalhada').select('*');
    const { data: analises } = await supabase.from('vw_analise_demanda').select('*');
    
    const totalDemandas = demandas?.length || 0;
    const demandasRecebidas = demandas?.filter(d => d.status_id === 1).length || 0;
    const demandasEncaminhadas = demandas?.filter(d => d.status_id === 3).length || 0;
    const demandasConcluidas = demandas?.filter(d => d.status_id === 2).length || 0;
    
    const totalAnalises = analises?.length || 0;
    const analisesPendentes = analises?.filter(a => a.analisedemanda_status_id === 1).length || 0;
    const analisesFinalizadas = analises?.filter(a => a.analisedemanda_status_id === 4).length || 0;
    
    const demandasApp = demandas?.filter(d => d.tipo_usuario_origem === 1).length || 0;
    const demandasWhatsApp = demandas?.filter(d => d.tipo_usuario_origem === 2).length || 0;

    return {
      totalDemandas,
      demandasRecebidas,
      demandasEncaminhadas,
      demandasConcluidas,
      totalAnalises,
      analisesPendentes,
      analisesFinalizadas,
      demandasApp,
      demandasWhatsApp,
      taxaResolucao: totalDemandas > 0 ? ((demandasConcluidas / totalDemandas) * 100).toFixed(1) : 0
    };
  };

  const carregarDemandasPorStatus = async () => {
    const { data } = await supabase.from('vw_demandas_por_status').select('*');
    return data || [];
  };

  const carregarDemandasPorSetor = async () => {
    const { data } = await supabase.from('vw_demandas_por_setor').select('*').limit(10);
    return data || [];
  };

  const carregarEvolucaoMensal = async () => {
    const { data } = await supabase.from('vw_evolucao_demandas_mes').select('*').order('mes');
    return data?.map(item => ({
      ...item,
      mes: new Date(item.mes).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' })
    })) || [];
  };

  const carregarTopBairros = async () => {
    const { data } = await supabase.from('vw_top7_bairros_demanda').select('*');
    return data || [];
  };

  const carregarTopCategorias = async () => {
    try {
      const { data, error } = await supabase.from('vw_top7_categorias_demandas').select('*');
      if (error) {
        console.error('Erro ao carregar categorias:', error);
        return [];
      }
      console.log('Categorias carregadas:', data);
      return data || [];
    } catch (error) {
      console.error('Erro ao carregar top categorias:', error);
      return [];
    }
  };

  const carregarMapaCalor = async () => {
    try {
      const { data, error } = await supabase.from('vw_demanda_mapa_calor').select('*');
      if (error) {
        console.error('Erro ao carregar mapa de calor:', error);
        return [];
      }
      console.log('Dados mapa de calor:', data);
      return data || [];
    } catch (error) {
      console.error('Erro ao carregar mapa de calor:', error);
      return [];
    }
  };

  const carregarMapaPontos = async () => {
    try {
      const { data, error } = await supabase.from('vw_mapa_demandas').select('*');
      if (error) {
        console.error('Erro ao carregar mapa de pontos:', error);
        return [];
      }
      console.log('Dados mapa de pontos:', data);
      return data || [];
    } catch (error) {
      console.error('Erro ao carregar mapa de pontos:', error);
      return [];
    }
  };

  const carregarTempoMedioResolucao = async () => {
    const { data } = await supabase.from('vw_tempo_medio_resolucao_dias').select('*').single();
    return data?.tempo_medio_resolucao_dias || 0;
  };

  // Cores para os gráficos
  const CORES_GRAFICOS = [
    '#D23727', '#e74c3c', '#f39c12', '#f1c40f', '#27ae60', 
    '#2ecc71', '#3498db', '#2980b9', '#9b59b6', '#8e44ad'
  ];

  const CORES_STATUS = {
    'RECEBIDA': '#f39c12',
    'CONCLUÍDA': '#27ae60', 
    'ENCAMINHADA': '#3498db',
    'CANCELADA': '#e74c3c'
  };

  if (loadingStats) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner-large"></div>
        <p>Carregando estatísticas...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-estatisticas-completo">
      {/* Header do Dashboard */}
      <div className="dashboard-header-completo">
        <div className="header-content1">
          <h1>Dashboard de Demandas</h1>
          <p>Visão geral e análise detalhada do sistema</p>
        </div>
        <div className="header-actions">
          <button className="btn-refresh" onClick={carregarEstatisticas}>
            <i className="fas fa-sync-alt"></i>
            Atualizar
          </button>
        </div>
      </div>

      {/* Alertas e Notificações */}
      <AlertasDashboard />

      {/* Métricas em Tempo Real */}
      <MetricasTempoReal />

      {/* Cards de Resumo Geral */}
      <div className="cards-resumo-geral">
        <div className="card-resumo card-total">
          <div className="card-icon">
            <i className="fas fa-clipboard-list"></i>
          </div>
          <div className="card-content">
            <h3>{estatisticas.resumoGeral.totalDemandas}</h3>
            <p>Total de Demandas</p>
            <span className="card-trend">
              <i className="fas fa-arrow-up"></i>
              +{estatisticas.resumoGeral.demandasRecebidas} este mês
            </span>
          </div>
        </div>

        <div className="card-resumo card-pendentes">
          <div className="card-icon">
            <i className="fas fa-clock"></i>
          </div>
          <div className="card-content">
            <h3>{estatisticas.resumoGeral.demandasRecebidas}</h3>
            <p>Demandas Recebidas</p>
            <span className="card-trend">
              <i className="fas fa-exclamation-triangle"></i>
              Aguardando análise
            </span>
          </div>
        </div>

        <div className="card-resumo card-concluidas">
          <div className="card-icon">
            <i className="fas fa-check-circle"></i>
          </div>
          <div className="card-content">
            <h3>{estatisticas.resumoGeral.demandasConcluidas}</h3>
            <p>Demandas Concluídas</p>
            <span className="card-trend">
              <i className="fas fa-arrow-up"></i>
              {estatisticas.resumoGeral.taxaResolucao}% resolvidas
            </span>
          </div>
        </div>

        <div className="card-resumo card-tempo">
          <div className="card-icon">
            <i className="fas fa-hourglass-half"></i>
          </div>
          <div className="card-content">
            <h3>{estatisticas.tempoMedioResolucao}</h3>
            <p>Dias (Tempo Médio)</p>
            <span className="card-trend">
              <i className="fas fa-calendar-alt"></i>
              Para resolução
            </span>
          </div>
        </div>

        <div className="card-resumo card-analises">
          <div className="card-icon">
            <i className="fas fa-search"></i>
          </div>
          <div className="card-content">
            <h3>{estatisticas.resumoGeral.totalAnalises}</h3>
            <p>Total de Análises</p>
            <span className="card-trend">
              <i className="fas fa-tasks"></i>
              {estatisticas.resumoGeral.analisesPendentes} pendentes
            </span>
          </div>
        </div>

        <div className="card-resumo card-origem">
          <div className="card-icon">
            <i className="fas fa-mobile-alt"></i>
          </div>
          <div className="card-content">
            <h3>{estatisticas.resumoGeral.demandasApp + estatisticas.resumoGeral.demandasWhatsApp}</h3>
            <p>Canais Digitais</p>
            <span className="card-trend">
              <i className="fab fa-whatsapp"></i>
              App + WhatsApp
            </span>
          </div>
        </div>
      </div>

      {/* Grid Principal de Gráficos */}
      <div className="dashboard-grid-principal">
        {/* Gráfico de Status - Pizza */}
        <div className="dashboard-card grafico-pizza">
          <div className="card-header">
            <h3>Demandas por Status</h3>
            <i className="fas fa-chart-pie"></i>
          </div>
          <div className="card-content">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={estatisticas.demandasPorStatus}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                  labelStyle={{ fontSize: '10px', fontWeight: 'bold' }}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="total_demandas"
                  nameKey="status"
                >
                  {estatisticas.demandasPorStatus.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={CORES_STATUS[entry.status] || CORES_GRAFICOS[index % CORES_GRAFICOS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Evolução Mensal - Linha */}
        <div className="dashboard-card grafico-linha">
          <div className="card-header">
            <h3>Evolução Mensal</h3>
            <i className="fas fa-chart-line"></i>
          </div>
          <div className="card-content">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={estatisticas.evolucaoMensal}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="mes" fontSize={10} />
                <YAxis fontSize={10} />
                <Tooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="demandas_criadas" 
                  stroke="#D23727" 
                  strokeWidth={3}
                  name="Demandas Criadas"
                />
                <Line 
                  type="monotone" 
                  dataKey="demandas_resolvidas" 
                  stroke="#27ae60" 
                  strokeWidth={3}
                  name="Demandas Resolvidas"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        

        {/* Demandas por Setor - Barras Verticais */}
        <div className="dashboard-card grafico-barras-vertical">
          <div className="card-header">
            <h3>Demandas por Setor</h3>
            <i className="fas fa-building"></i>
          </div>
          <div className="card-content">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={estatisticas.demandasPorSetor.slice(0, 8)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="setor_descricao" 
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  fontSize={9}
                  interval={0}
                />
                <YAxis fontSize={10} />
                <Tooltip />
                <Bar dataKey="total_demandas" fill="#3498db" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Bairros - Área */}
        <div className="dashboard-card grafico-area">
          <div className="card-header">
            <h3>Top 7 Bairros</h3>
            <i className="fas fa-map-marker-alt"></i>
          </div>
          <div className="card-content">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={estatisticas.topBairros}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="bairro" angle={-45} textAnchor="end" height={80} fontSize={9} interval={0} />
                <YAxis fontSize={10} />
                <Tooltip />
                <Area 
                  type="monotone" 
                  dataKey="total_demandas" 
                  stroke="#f39c12" 
                  fill="#f39c12" 
                  fillOpacity={0.6}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Origem das Demandas - Pizza Pequena */}
        <div className="dashboard-card grafico-origem">
          <div className="card-header">
            <h3>Origem das Demandas</h3>
            <i className="fas fa-mobile-alt"></i>
          </div>
          <div className="card-content">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={[
                    { name: 'Aplicativo', value: estatisticas.resumoGeral.demandasApp },
                    { name: 'WhatsApp', value: estatisticas.resumoGeral.demandasWhatsApp }
                  ]}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  <Cell fill="#27ae60" />
                  <Cell fill="#25d366" />
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

    </div>
  );
}